import { FormSectionHandler } from '../entity.handler';
import { SslCertificateResponse, SslCertificateUpdateRequest, IdNameType, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, MemoField, SearchSelectField, ComboField, IdNameComboValueHandlers, EMPTY_GUID } from '../utils';
import { ICONS } from '../icons';

interface EditorValues {
    company?: IdNameType;
    domain?: string;
    theme?: IdName;
    notes?: string;
}

export class SslCertificateDetailsSectionHandler implements FormSectionHandler<SslCertificateResponse, EditorValues> {

    type = 'form';
    icon = ICONS.sslcertificate;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    actions = [
        { id: 'validate', name: this.translate('VALIDATE'), showAlways: true, showEditing: false },
        { id: 'provision', name: this.translate('PROVISION'), showAlways: true, showEditing: false },
    ];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public creating: boolean
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.SSLCERTIFICATE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: SslCertificateResponse, payload: SslCertificateUpdateRequest): Promise<SslCertificateResponse> {
        return this.admin.applyUpdate(api => api.entities.updateSslCertificate(entity.id, payload));
    }

    async mapEntityToEditor(entity: SslCertificateResponse): Promise<EditorValues> {
        if (this.creating) {
            return {
                domain: undefined,
                notes: undefined,
                company: {
                    id: '',
                    name: '',
                    type: '',
                },
                theme: {
                    id: EMPTY_GUID
                }
            };
        }

        const result = {
            domain: entity.domain,
            notes: entity.notes,
            theme: entity.theme || { id: EMPTY_GUID, name: 'Inherited' },
            company: { id: entity?.company?.id, name: this.getCompanyName(entity.company), type: entity?.company?.type },
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return result;
    }

    async mapEditorToEntity(entity: SslCertificateUpdateRequest, values: EditorValues): Promise<SslCertificateUpdateRequest> {
        const patch: SslCertificateUpdateRequest = {
            domain: values.domain,
            notes: values.notes,
            company: values.company,
            theme: values.theme,
        };
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object
        return EntityUtils.mergeFields(['domain', 'notes', 'company', 'theme'], patch, entity);
    }

    async getFields(entity: SslCertificateResponse): Promise<FormBuilderField[]> {


        const themes = entity?.company?.id && await this.admin.getCached(`listThemes/${entity.company.id}/true/0/100/name`, api => api.entities.listThemes(entity.company.id, true, 0, 100, 'name'));

        const fields: FormBuilderField[] = [
            {
                ...TextField('domain', this.translate('FIELDS.DOMAIN'), true, 1, 255),
                readonly: !this.creating,
                getText: (field, values) => {
                    const domain = values[field.id].split('.').filter(x => x).join('.');
                    if (this.creating) return domain;
                    return `<a href="https://${domain}" target="_blank">${domain}</a>`;
                }
            },
            {
                ...SearchSelectField('company', this.translate('FIELDS.COMPANY'), true, {
                    searchListPromise: async (_limit, filter) => {
                        filter = `name=*${filter}*`;
                        const [distributorResponse, vendorResponse, clientResponse] = await this.admin.getCached(`childCompanies:${this.ownerId}:filter:${filter}`, async api => {
                            return Promise.all([
                                api.accounts.listDistributors(this.ownerId, undefined, undefined, undefined, filter),
                                api.accounts.listVendors(this.ownerId, undefined, undefined, undefined, filter),
                                api.accounts.listClients(this.ownerId, undefined, undefined, undefined, filter),
                            ]);
                        })
                        return {
                            items: [
                                ...distributorResponse.items.map(x => ({ id: x.id, name: this.getCompanyName({ ...x, type: 'distributor' }) })),
                                ...vendorResponse.items.map(x => ({ id: x.id, name: this.getCompanyName({ ...x, type: 'vendor' }) })),
                                ...clientResponse.items.map(x => ({ id: x.id, name: this.getCompanyName({ ...x, type: 'client' }) })),
                            ],
                            count: distributorResponse.count + vendorResponse.count + clientResponse.count
                        };
                    },
                    searchItemPromise: id => this.admin.getCached(`getCompany/${id}`, api => api.accounts.getCompany(id)).then(x => ({ id: x.id, name: x.name })),
                }),
                getText: (field, values) => {
                    const id = values[field.id]?.id;
                    const name = values[field.id]?.name;
                    const type = values[field.id]?.type;
                    if (this.creating) return name;
                    return `<a href="#/admin/${type}/${id}/details">${name}</a>`;
                }
            },
            ComboField('theme', this.translate('FIELDS.THEME'), false, [{ key: EMPTY_GUID, value: 'Inherited' }, ...(themes?.items || []).filter(x => x['state'] === 'active').map(x => ({ key: x.id, value: x.name }))], IdNameComboValueHandlers),
            MemoField('notes', this.translate('FIELDS.NOTES'), false, 0, 1024)
        ].filter(x => x);
        return EntityUtils.prepareBulk(!entity, [], fields);

    }

    getCompanyName(entity: IdNameType): string {
        if (!entity) return '';
        return `${entity.name} (${this.admin.i18n.instant('ADMIN.ENTITIES.' + entity.type.toUpperCase())})`;
    }

    async actionTriggered(entity: SslCertificateResponse, action: string): Promise<void> {
        const icon = (icon, color) => `<i class="icon icon-${icon} text-${color} icon-fw" style="font-size: 2rem;"></i>`;
        if (action === 'validate') {
            await this.admin.applyUpdate(async (api) => {
                const response = await api.entities.validateSslCertificate(entity.id)

                const provisioning = response.provisioned
                    ? `${icon('circle-check', 'success')} SSL Certificate has been provisioned`
                    : `${icon('circle-xmark', 'danger')} SSL certificate hasn't been provisioned yet (use the Provision action to do so).`;

                const verified = response.verified
                    ? `${icon('circle-check', 'success')} CNAME has been verified`
                    : `${icon('circle-xmark', 'danger')} CNAME has either not propagated yet, or the domain is not CNAME'd correctly.`;


                const message = `${verified}<br/>${provisioning}`;
                await this.admin.modal.alert(message, 'SSL Certificate Validation');
            })
        } else if (action === 'provision') {
            const result = await this.admin.modal.confirm(this.translate('MODALS.PROVISION.DESC'));
            if (result) {
                await this.admin.applyUpdate(async (api) => {
                    const response = await api.entities.provisionSslCertificate(entity.id)
                    const heading = this.admin.i18n.instant(response.success ? "DIALOG.SUCCESS" : "DIALOG.ERROR");
                    const message = response.success ? this.translate("MODALS.PROVISION.SUCCESS.DESC") : response.errors.join('<br/>');
                    await this.admin.modal.alert(message, heading);
                })
            }
        }
    }

}
